import { FC, Fragment, useEffect, useState } from 'react';
import { Col, FormInstance, Grid, Image, Row } from 'antd';
import { useApp } from '@/App';
import FormBuilder, { useFormBuilder } from '@/components/FormBuilder';
import formRule from '@/libs/antd/rules';
import fleetApi from '@/libs/fleet-api/fetcher';
import { AuthPayload, authenticate } from '@/libs/fleet-api/services/users';
import loginBg from '@/assets/login-bg.jpg';

type AdminLoginForm = AuthPayload & {
	rememberMe?: boolean;
};

const AdminLoginPage: FC = () => {
	const screen = Grid.useBreakpoint();
	const { navigate, notification, queryClient } = useApp();
	const { form } = useFormBuilder<AdminLoginForm>();
	const { setFieldsValue } = form;
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		const storedLoginForm = localStorage.getItem('adminRememberMe');
		if (storedLoginForm) {
			const data = JSON.parse(storedLoginForm) as AdminLoginForm;
			setFieldsValue(data);
		}
	}, [setFieldsValue]);

	const handleSubmit = async (payload: AdminLoginForm) => {
		const { userName, password, rememberMe } = payload;
		if (rememberMe) {
			localStorage.setItem(
				'adminRememberMe',
				JSON.stringify({ userName, password, rememberMe }),
			);
		} else {
			localStorage.removeItem('adminRememberMe');
		}

		setIsLoading(true);
		try {
			await authenticate({ userName, password });
			await queryClient.invalidateQueries();
			navigate('AdminDashboardPage');
		} catch (error) {
			if (fleetApi.isError(error)) {
				const message = await error.json();
				notification.error({
					duration: 20,
					message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
					description: (
						<div>
							{message?.metadata?.debugInfo?.messages?.length ? (
								<ul>
									{message?.metadata?.debugInfo?.messages.map((msg: string) => (
										<li key={msg}>{msg}</li>
									))}
								</ul>
							) : (
								<pre>Ops, Ada yang salah</pre>
							)}
						</div>
					),
				});
			}
		}
		setIsLoading(false);
	};

	return (
		<Fragment>
			<div style={{ padding: screen.md ? 76 : 38 }}>
				<Row
					gutter={[76, 38]}
					align={'middle'}
				>
					<Col
						xs={{ span: 24 }}
						md={{ span: 12 }}
					>
						<Image
							src={loginBg}
							preview={false}
						/>
					</Col>
					<Col
						xs={{ span: 24 }}
						md={{ span: 12 }}
					>
						<div style={{ textAlign: 'center', marginBottom: 24 }}>
							<h1 style={{ color: '#1890ff', marginBottom: 8 }}>
								Admin Fleet Management
							</h1>
							<p style={{ color: '#666' }}>
								Sistem manajemen pengguna dan organisasi
							</p>
						</div>
						<FormBuilder
							form={form as FormInstance<unknown>}
							config={{
								variant: 'filled',
							}}
							onFinish={handleSubmit}
							fields={[
								{
									name: 'userName',
									label: 'Username',
									type: 'text',
									config: {
										placeholder: 'Masukkan username',
										size: 'large',
									},
									rules: [formRule.required('Username wajib diisi')],
								},
								{
									name: 'password',
									label: 'Password',
									type: 'password',
									config: {
										placeholder: 'Masukkan password',
										size: 'large',
									},
									rules: [formRule.required('Password wajib diisi')],
								},
								{
									type: 'button',
									width: '1/2',
									config: {
										children: 'Lupa Password?',
										block: false,
										type: 'link',
										style: { padding: 0 },
									},
								},
								{
									name: 'rememberMe',
									type: 'checkbox',
									width: '1/2',
									config: {
										children: 'Ingatkan Saya',
										style: {
											display: 'flex',
											justifyContent: 'flex-end',
										},
									},
								},
								{
									type: 'button',
									config: {
										loading: isLoading,
										type: 'primary',
										htmlType: 'submit',
										children: 'Login Admin',
										size: 'large',
										shape: 'round',
									},
								},
							]}
						/>
					</Col>
				</Row>
			</div>
		</Fragment>
	);
};

export default AdminLoginPage;
